import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, concat, forkJoin, from, toArray } from 'rxjs';
import { ConfigService } from 'src/app/shared/services';
import { timeout, tap, switchMap } from 'rxjs/operators';
import { throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class WorkflowDataService {
  private apiUrl: string;
  private workflowItemsSubject = new BehaviorSubject<any[]>([]);
  workflowItems$ = this.workflowItemsSubject.asObservable();

  constructor(
    private http: HttpClient,
    private configService: ConfigService
  ) {
    this.apiUrl = this.configService.getConfig()['apiURL']; // e.g., 'http://localhost:1337/api'
    this.loadWorkflowItems();
  }

  // Load all workflows from the backend
  loadWorkflowItems(): void {
    this.http.get<{ success: boolean; data: any[] }>(`${this.apiUrl}workflows`).subscribe({
      next: (response) => {
        // Update BehaviorSubject with the data array from the response
        this.workflowItemsSubject.next(response.data || []);
      },
      error: (err) => {
        console.error('Failed to load workflows:', err);
        // Optionally, update with empty array or handle error state
        this.workflowItemsSubject.next([]);
      }
    });
  }
  clearTables(): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.apiUrl}workflows/clear`, {});
  }
async convertFileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    // Validate input
    if (!file || !(file instanceof File)) {
      reject(new Error('Invalid file provided'));
      return;
    }
    
    // Check file size (optional - add your size limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      reject(new Error(`File size exceeds limit. Maximum allowed: ${maxSize / 1024 / 1024}MB`));
      return;
    }
    
    const reader = new FileReader();
    
    reader.onload = () => {
      const result = reader.result as string;
      console.log(`File converted to base64 - Name: ${file.name}, Type: ${file.type}, Size: ${file.size}, Base64 length: ${result.length}`);
      resolve(result);
    };
    
    reader.onerror = (error) => {
      console.error('FileReader error:', error);
      reject(new Error(`Error reading file: ${file.name}`));
    };
    
    reader.onabort = () => {
      console.error('File reading was aborted');
      reject(new Error(`File reading was aborted for: ${file.name}`));
    };
    
    // readAsDataURL works for all file types including PDF, JPG, PNG
    reader.readAsDataURL(file);
  });
}
private async processWorkflowFiles(workflow: any): Promise<void> {
  // Process payment slips (now an array of slips per payment)
  if (workflow.data?.payments?.length > 0) {
    for (let i = 0; i < workflow.data.payments.length; i++) {
      const payment = workflow.data.payments[i];
      
      // Handle the new paymentSlips array structure
      if (payment.paymentSlips && Array.isArray(payment.paymentSlips)) {
        console.log(`Processing ${payment.paymentSlips.length} payment slips for payment ${i + 1}`);
        
        for (let j = 0; j < payment.paymentSlips.length; j++) {
          const slip = payment.paymentSlips[j];
          
          // Check if this slip has a File object to convert
          if (slip.file && slip.file instanceof File) {
            try {
              console.log(`Converting payment slip ${j + 1} for payment ${i + 1}: ${slip.file.name} (${slip.file.type})`);
              
              // Validate file type
              const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
              if (!allowedTypes.includes(slip.file.type)) {
                console.warn(`Unsupported file type: ${slip.file.type}`);
                continue;
              }
              
              // Convert to base64 and replace the File object
              const base64Data = await this.convertFileToBase64(slip.file);
              
              // Store the base64 data and preserve metadata
              payment.paymentSlips[j] = {
                ...slip, // Keep existing properties like id, name, size, type
                file: null, // Remove the File object
                base64Data: base64Data, // Store base64 data
                originalFileName: slip.file.name,
                originalFileSize: slip.file.size,
                originalFileType: slip.file.type
              };
              
              console.log(`Successfully converted payment slip ${j + 1} for payment ${i + 1} to base64`);
              
            } catch (error) {
              console.error(`Error converting payment slip ${j + 1} for payment ${i + 1} to base64:`, error);
              // Optionally remove the problematic file or mark it as failed
              // payment.paymentSlips[j].conversionFailed = true;
            }
          } else if (slip.file && typeof slip.file === 'object' && Object.keys(slip.file).length === 0) {
            // Handle empty file objects (like the second file in your example)
            console.warn(`Empty file object found in payment ${i + 1}, slip ${j + 1}. Removing...`);
            payment.paymentSlips.splice(j, 1);
            j--; // Adjust index after removal
          }
        }
      }
      
      // Legacy support: Handle old single paymentSlip property
      if (payment.paymentSlip instanceof File) {
        try {
          console.log(`Converting legacy payment slip ${i + 1}: ${payment.paymentSlip.name} (${payment.paymentSlip.type})`);
          
          // Validate file type
          const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
          if (!allowedTypes.includes(payment.paymentSlip.type)) {
            console.warn(`Unsupported file type: ${payment.paymentSlip.type}`);
            continue;
          }
          
          // Convert to base64
          payment.paymentSlip = await this.convertFileToBase64(payment.paymentSlip);
          console.log(`Successfully converted legacy payment slip ${i + 1} to base64`);
          
        } catch (error) {
          console.error(`Error converting legacy payment slip ${i + 1} to base64:`, error);
        }
      }
    }
  }
  
  // Process other file types if needed (invoices, etc.)
  if (workflow.data?.invoices?.length > 0) {
    for (let i = 0; i < workflow.data.invoices.length; i++) {
      const invoice = workflow.data.invoices[i];
      
      if (invoice.someFile instanceof File) {
        try {
          invoice.someFile = await this.convertFileToBase64(invoice.someFile);
        } catch (error) {
          console.error(`Error converting invoice file ${i + 1}:`, error);
        }
      }
    }
  }
}
updateWorkflowItem(workflow: any): Observable<any> {
  return from(this.processWorkflowFiles(workflow)).pipe(
    switchMap(() => {
      const formData = new FormData();
      formData.append('orderId', workflow.orderId);
      formData.append('currentStep', workflow.currentStep);
      formData.append('completed', workflow.completed);
      formData.append('data', JSON.stringify(workflow.data));
      formData.append('stepIds', JSON.stringify(workflow.stepIds));
      
      console.log('Submitting workflow:', workflow);
      return this.http.post(`${this.apiUrl}workflows`, formData, {
        responseType: 'json',
      });
    })
  );
}
// In your workflow data service
checkConflicts(workflow: any): Observable<any> {
  // Add validation before sending
  if (!workflow || !workflow.quotationNo) {
    console.error('Invalid workflow data before sending:', workflow);
    return throwError(() => ({ 
      status: 400, 
      error: { message: 'Invalid workflow data' },
      message: 'Invalid workflow data'
    }));
  }
  
  console.log('Sending conflict check request:', {
    url: `${this.apiUrl}workflows/check-conflicts`,
    quotationNo: workflow.quotationNo,
    dataSize: JSON.stringify(workflow).length
  });
  
  return this.http.post(`${this.apiUrl}workflows/check-conflicts`, workflow, {
    headers: {
      'Content-Type': 'application/json'
    }
  }).pipe(
    timeout(30000), // 30 second timeout using RxJS operator
    tap(response => {
      console.log('Conflict check response:', response);
    }),
    catchError(error => {
      console.error('Conflict check request failed:', {
        error,
        workflow: workflow.quotationNo,
        url: error.url,
        status: error.status
      });
      return throwError(() => error);
    })
  );
}

  // Get a single workflow by orderId
  getWorkflow(orderId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}workflows/${orderId}`);
  }

  // Delete a workflow
  deleteWorkflow(orderId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}workflows/${orderId}`);
  }

  // Update local workflow items (used by QuotationListComponent)
  updateWorkflowItems(items: any[]): void {
    this.workflowItemsSubject.next(items);
  }
  // Batch insert workflows (50 items at a time)
  batchInsertWorkflows(workflows: any[]): Observable<any> {
    // Create an array of HTTP POST requests for each workflow
    const requests = workflows.map(workflow =>
      this.http.post(`${this.apiUrl}workflows/batch`, [workflow]) // Send each workflow as a single-item array
    );

    // Use concat to execute requests sequentially
    return concat(...requests).pipe(
      // Collect all results
      toArray(),
      // Handle errors
      catchError(err => {
        console.error('Individual workflow insert error:', err);
        throw err;
      })
    );
  }

  saveQuotationStatus(quotationNo: string, status: string, statusId?: string): Observable<any> {
    const payload: any = { quotationNo, status };
    if (statusId) {
      payload.statusId = statusId; // Include statusId if provided
    }
    return this.http.post(`${this.apiUrl}workflows/status`, payload).pipe(
      catchError(err => {
        console.error('Error saving quotation status:', err);
        throw err;
      })
    );
  }

/**
 * Upload KIK#F1 data (parsed JSON data, not file)
 * @param data - Array of parsed KIK#F1 records
 * @returns Observable with upload results
 */
uploadKikF1Data(data: any[]): Observable<any> {
  const payload = {
    kikF1Data: data
  };
  
  console.log('Uploading KIK#F1 data:', payload);
  
  return this.http.post(`${this.apiUrl}workflows/upload-kikf1`, payload, {
    headers: {
      'Content-Type': 'application/json'
    }
  }).pipe(
    tap(response => {
      console.log('KIK#F1 data upload response:', response);
    }),
    catchError(error => {
      console.error('KIK#F1 data upload failed:', error);
      return throwError(() => error);
    })
  );
}

  // KIK#F1 Data Methods
  getAllKikF1Data(): Observable<any> {
    console.log('Fetching ALL KIK#F1 data');
    
    return this.http.get(`${this.apiUrl}workflows/kikf1/all`).pipe(
      tap(response => {
        console.log('All KIK#F1 data response:', response);
      }),
      catchError(error => {
        console.error('Failed to fetch all KIK#F1 data:', error);
        return throwError(() => error);
      })
    );
  }

  getKikF1DataByOrderId(orderId: string): Observable<any> {
    console.log(`Fetching KIK#F1 data for order: ${orderId}`);
    
    return this.http.get(`${this.apiUrl}workflows/${orderId}/kikf1`).pipe(
      tap(response => {
        console.log('KIK#F1 data response for order:', orderId, response);
      }),
      catchError(error => {
        console.error(`Failed to fetch KIK#F1 data for order ${orderId}:`, error);
        return throwError(() => error);
      })
    );
  }

  deleteKikF1Data(orderId: string): Observable<any> {
    console.log(`Deleting KIK#F1 data for order: ${orderId}`);
    
    return this.http.delete(`${this.apiUrl}workflows/${orderId}/kikf1`).pipe(
      tap(response => {
        console.log('KIK#F1 data delete response:', response);
      }),
      catchError(error => {
        console.error(`Failed to delete KIK#F1 data for order ${orderId}:`, error);
        return throwError(() => error);
      })
    );
  }

  updateKikF1Data(kikF1Id: string, data: any): Observable<any> {
    console.log(`Updating KIK#F1 data: ${kikF1Id}`, data);
    
    return this.http.put(`${this.apiUrl}workflows/kikf1/${kikF1Id}`, data).pipe(
      tap(response => {
        console.log('KIK#F1 data update response:', response);
      }),
      catchError(error => {
        console.error(`Failed to update KIK#F1 data ${kikF1Id}:`, error);
        return throwError(() => error);
      })
    );
  }

}
